import * as THREE from "three"
import { Vector3 } from "three";

export class LinearDimension {
    constructor(domRoot, renderer, camera) {
        this._target = null;

        this._domRoot = domRoot;
        this._renderer = renderer;
        this._camera = camera;

        this._old_camera_position = '';

        this.cb = {
            onChange: []
        };

        this.config = {
            units: "cm",
            unitsConverter: function(v) {
                return Math.round(v * 100, 0);
            }
        };

        this._init();
    }

    _init() {
        let parentSize = this._domRoot.getBoundingClientRect();

        let el = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        el.setAttributeNS("http://www.w3.org/2000/xmlns/", "xmlns:xlink", "http://www.w3.org/1999/xlink");
        el.setAttribute('viewBox', '0 0 ' + parentSize.width + " " + parentSize.height);

        el.classList.add("dim");
        el.innerHTML = "";
        this._domRoot.appendChild(el);
        this._domElement = el;

        window.addEventListener('resize', () => {        
            parentSize = this._domRoot.getBoundingClientRect();
            el.setAttribute('viewBox', '0 0 ' + parentSize.width + " " + parentSize.height);
        });
    }

    setTarget(target) {
        this._target = target;
        // const modelBound = if(this._target) this._target.getModelBound();
    }

    create() {
        this.update(this._camera);
    }

    show() {
        if(!this._target) return false;
        if(this._domElement) this._domElement.classList.add('show');
        this._old_camera_position = null;
    }

    hide() {
        if(this._domElement) this._domElement.classList.remove('show')
        if(!this._target) return false;
    }

    update(camera) {
        this._camera = camera;

        let parentSize = this._domRoot.getBoundingClientRect();
        if(!this._last_size || (parentSize.width != this._last_size.width || parentSize.height != this._last_size.height)) {
            this._domElement.setAttribute('viewBox', '0 0 ' + parentSize.width + " " + parentSize.height);
            this._last_size = {width:parentSize.width, height:parentSize.height};
        }

        // reposition label
        if (this._camera && this._domElement !== undefined && this._target) {
            const nowKey = Math.round(this._camera.position.x * 100, 2)/100 + 'x' + Math.round(this._camera.position.y * 100, 2)/100 + 'x' + Math.round(this._camera.position.z * 100, 2)/100;

            if(this._old_camera_position != nowKey) {
                const screenPositions = this.lineToScreenPositions();

                let html = '';
                const infoSize = this._target.getSize();
                for (const [key, value] of Object.entries(screenPositions)) {
                    const centerSize = infoSize && infoSize[key] ? infoSize[key] : this.config.unitsConverter(value.size);

                    const textPos = {
                        x: value.center.x,
                        y: value.center.y
                    };

                    // NTODO : 선의 방향에 따라 textAnchor 바꾸기
                    html += `<text transform="" text-anchor="" dominant-baseline="middle" x="${textPos.x}" y="${textPos.y}">${centerSize}${this.config.units}</text>`;
                    html += `<line x1="${value.from.x}" y1="${value.from.y}" x2="${value.to.x}" y2="${value.to.y}" style="stroke: rgb(0, 0, 0); stroke-width: 1.5;"></line>`;
                }

                this._domElement.innerHTML = html;
                this._old_camera_position = nowKey;
            }
        }

        return false;
    }

    detach() {
        if (this._domElement !== undefined) {
            this._domRoot.removeChild(this._domElement);
            this._domElement = undefined;
        }
    }

    lineToScreenPositions() {
        if(!this._target) return false;

        const renderer = this._renderer;
        const camera = this._camera;

        let widthHalf = 0.5*this._domElement.clientWidth;
        let heightHalf = 0.5*this._domElement.clientHeight;

        const screenPositions = {
            x: {
                from: null,
                to: null,
                center: null,
                size: 0
            },
            y: {
                from: null,
                to: null,
                center: null,
                size: 0
            },
            z: {
                from: null,
                to: null,
                center: null,
                size: 0
            }
        };
        
        const obj = this._target.getModel();
        obj.updateMatrixWorld();

        const size = this._target.getModelSize();

        let vector = new THREE.Vector3();
        vector.setFromMatrixPosition(obj.matrixWorld);

        const margin = 0.1; // 모델과 표시선 사이 여백

        screenPositions.x.from = vector.clone().add(new Vector3(-size.x/2, 0, size.z/2 + margin));
        screenPositions.x.to = vector.clone().add(new Vector3(size.x/2, 0, size.z/2 + margin));
        screenPositions.x.center = screenPositions.x.from.clone().add(screenPositions.x.to).multiplyScalar(0.5);
        screenPositions.x.center.z += margin;
        screenPositions.x.size = size.x;

        screenPositions.y.from = vector.clone().add(new Vector3(size.x/2 + margin, size.y, -size.z/2 - margin/2));
        screenPositions.y.to = vector.clone().add(new Vector3(size.x/2 + margin, 0, -size.z/2 - margin/2));
        screenPositions.y.center = screenPositions.y.from.clone().add(screenPositions.y.to).multiplyScalar(0.5);
        screenPositions.y.center.x += margin/2;
        screenPositions.y.size = size.y;

        screenPositions.z.from = vector.clone().add(new Vector3(size.x/2 + margin, 0, -size.z/2));
        screenPositions.z.to = vector.clone().add(new Vector3(size.x/2 + margin, 0, size.z/2));
        screenPositions.z.center = screenPositions.z.from.clone().add(screenPositions.z.to).multiplyScalar(0.5);
        screenPositions.z.center.x += margin/2;
        screenPositions.z.size = size.z;

		camera.updateMatrixWorld(); // FIX

        for (const [key, value] of Object.entries(screenPositions)) {
            if(value.from && value.to) {
                value.from.project(camera);
                value.to.project(camera);

                value.from.x = ( value.from.x + 1) * widthHalf;
                value.from.y = - ( value.from.y - 1) * heightHalf;
                value.to.x = ( value.to.x + 1) * widthHalf;
                value.to.y = - ( value.to.y - 1) * heightHalf;
            }

            if(value.center) {
                value.center.project(camera);

                value.center.x = ( value.center.x + 1) * widthHalf;
                value.center.y = - ( value.center.y - 1) * heightHalf;
            }
        }

        return screenPositions;
    }
}
